import { useCallback, useEffect, useState } from 'react';
import type { TemplateItemInput, TemplateWithItems } from '@epasu/core';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';

interface Props {
  template?: TemplateWithItems;
  onClose: () => void;
  onSaved: () => void;
}

/** Eine Zeile im Bearbeiten, bevor sie zur Vorlage wird. */
interface Zeile {
  kind: 'phase' | 'task';
  title: string;
  /** Index der Phase in dieser Liste; nur bei einer Aufgabe. */
  parentIndex: number | null;
  /** Tage ab Projektstart, als Text, damit das Feld leer sein darf. */
  offsetDays: string;
}

/**
 * Vorlage anlegen und ändern.
 *
 * Die Posten stehen als flache, sortierbare Liste da und nicht als Baum:
 * eine Vorlage hat selten mehr als ein Dutzend Zeilen, und eine Liste mit
 * einer Spalte „gehört zu“ ist leichter zu überblicken — und zu bedienen —
 * als ein Baum, den man auf- und zuklappen muss.
 *
 * Die Zugehörigkeit zeigt auf den **Index** in dieser Liste, nicht auf eine
 * Id: beim Anlegen gibt es noch keine Ids, und beim Ändern werden die Posten
 * ohnehin ersetzt. Der Dienst setzt die Ids, wenn er schreibt.
 */
export function ProjectTemplateDialog({ template, onClose, onSaved }: Props) {
  const [name, setName] = useState(template?.name ?? '');
  const [notiz, setNotiz] = useState(template?.notes ?? '');
  const [zeilen, setZeilen] = useState<Zeile[]>(() => ausVorlage(template));
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const hinzu = (kind: 'phase' | 'task') =>
    setZeilen((z) => [...z, { kind, title: '', parentIndex: null, offsetDays: '' }]);

  const aendern = (index: number, patch: Partial<Zeile>) =>
    setZeilen((z) => z.map((zeile, i) => (i === index ? { ...zeile, ...patch } : zeile)));

  const entfernen = (index: number) =>
    setZeilen((z) =>
      z
        .filter((_, i) => i !== index)
        // Verweise auf die entfernte Phase laufen sonst ins Leere, und die
        // dahinter verschieben sich um eins.
        .map((zeile) => {
          if (zeile.parentIndex === null) return zeile;
          if (zeile.parentIndex === index) return { ...zeile, parentIndex: null };
          return zeile.parentIndex > index
            ? { ...zeile, parentIndex: zeile.parentIndex - 1 }
            : zeile;
        }),
    );

  const schieben = (index: number, richtung: -1 | 1) => {
    const ziel = index + richtung;
    setZeilen((z) => {
      if (ziel < 0 || ziel >= z.length) return z;
      const neu = [...z];
      [neu[index], neu[ziel]] = [neu[ziel] as Zeile, neu[index] as Zeile];
      // Die Zugehörigkeit zeigt auf Indizes — beim Tausch wandern die mit.
      return neu.map((zeile) => {
        if (zeile.parentIndex === null) return zeile;
        if (zeile.parentIndex === index) return { ...zeile, parentIndex: ziel };
        if (zeile.parentIndex === ziel) return { ...zeile, parentIndex: index };
        return zeile;
      });
    });
  };

  const speichern = useCallback(async () => {
    const titel = name.trim();
    if (!titel) {
      setFehler('Die Vorlage braucht einen Namen.');
      return;
    }
    const leer = zeilen.findIndex((z) => !z.title.trim());
    if (leer !== -1) {
      setFehler(`Zeile ${leer + 1} hat keinen Titel.`);
      return;
    }

    const items: TemplateItemInput[] = zeilen.map((z) => ({
      kind: z.kind,
      title: z.title.trim(),
      parentIndex: z.kind === 'task' ? z.parentIndex : null,
      offsetDays: z.offsetDays.trim() ? Number.parseInt(z.offsetDays, 10) : null,
    }));

    const ungueltig = items.findIndex(
      (i) => i.offsetDays !== null && !Number.isInteger(i.offsetDays),
    );
    if (ungueltig !== -1) {
      setFehler(`Zeile ${ungueltig + 1}: Tage müssen eine ganze Zahl sein.`);
      return;
    }

    setBusy(true);
    setFehler(null);
    try {
      if (template) {
        await crm.projectTemplates.update(template.id, {
          name: titel,
          notes: notiz.trim() || null,
          items,
        });
      } else {
        await crm.projectTemplates.create({
          name: titel,
          notes: notiz.trim() || null,
          items,
        });
      }
      onSaved();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      setBusy(false);
    }
  }, [name, notiz, onSaved, template, zeilen]);

  const phasen = zeilen
    .map((z, i) => ({ ...z, index: i }))
    .filter((z) => z.kind === 'phase');

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6">
      <div className="w-full max-w-2xl space-y-4 rounded-lg bg-card p-6 shadow-xl">
        <h2 className="text-lg font-semibold">
          {template ? 'Vorlage bearbeiten' : 'Neue Projektvorlage'}
        </h2>

        <label className="block space-y-1 text-sm">
          <span className="font-medium">Name</span>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Website"
            autoFocus
          />
        </label>

        <label className="block space-y-1 text-sm">
          <span className="font-medium">Notiz</span>
          <Input
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            placeholder="Wofür diese Vorlage gedacht ist"
          />
        </label>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Phasen und Aufgaben</span>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => hinzu('phase')}>
                <Plus className="mr-1 h-3.5 w-3.5" />
                Phase
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => hinzu('task')}
                disabled={phasen.length === 0 && zeilen.length === 0}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Aufgabe
              </Button>
            </div>
          </div>

          {zeilen.length === 0 ? (
            <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
              Noch nichts drin. „Phase“ legt den ersten Abschnitt an, „Aufgabe“ hängt
              etwas darunter.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {zeilen.map((z, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span
                    className={`w-16 shrink-0 text-xs ${
                      z.kind === 'phase'
                        ? 'font-semibold uppercase tracking-wide text-muted-foreground'
                        : 'pl-3 text-muted-foreground'
                    }`}
                  >
                    {z.kind === 'phase' ? 'Phase' : 'Aufgabe'}
                  </span>

                  <Input
                    className="h-8 flex-1 text-sm"
                    value={z.title}
                    onChange={(e) => aendern(i, { title: e.target.value })}
                    placeholder={z.kind === 'phase' ? 'Konzept' : 'Briefing einholen'}
                    aria-label={`Titel Zeile ${i + 1}`}
                  />

                  {z.kind === 'task' && (
                    <Select
                      className="h-8 w-32 text-xs"
                      value={z.parentIndex === null ? '' : String(z.parentIndex)}
                      onChange={(e) =>
                        aendern(i, {
                          parentIndex: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      aria-label={`Phase für Zeile ${i + 1}`}
                    >
                      <option value="">Ohne Phase</option>
                      {phasen.map((p) => (
                        <option key={p.index} value={p.index}>
                          {p.title.trim() || `Phase ${p.index + 1}`}
                        </option>
                      ))}
                    </Select>
                  )}

                  <Input
                    className="h-8 w-20 text-sm"
                    value={z.offsetDays}
                    onChange={(e) => aendern(i, { offsetDays: e.target.value })}
                    placeholder="Tage"
                    inputMode="numeric"
                    aria-label={`Tage ab Start, Zeile ${i + 1}`}
                  />

                  <div className="flex shrink-0 gap-0.5">
                    <button
                      type="button"
                      onClick={() => schieben(i, -1)}
                      disabled={i === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                      aria-label={`Zeile ${i + 1} nach oben`}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => schieben(i, 1)}
                      disabled={i === zeilen.length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                      aria-label={`Zeile ${i + 1} nach unten`}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => entfernen(i)}
                      className="ml-1 text-muted-foreground hover:text-danger"
                      aria-label={`Zeile ${i + 1} entfernen`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <p className="text-xs text-muted-foreground">
            „Tage“ ist der Abstand zum Projektstart, nicht ein Datum — eine Vorlage mit
            festem Datum wäre nächstes Jahr falsch. Leer heißt: ohne Frist.
          </p>
        </div>

        {fehler && <p className="text-sm text-danger">{fehler}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button onClick={() => void speichern()} disabled={busy}>
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Die gespeicherten Posten zurück in die flache Bearbeitungsliste. */
function ausVorlage(template?: TemplateWithItems): Zeile[] {
  if (!template) return [];

  const sortiert = [...template.items].sort((a, b) => a.position - b.position);
  const indexById = new Map(sortiert.map((item, i) => [item.id, i]));

  return sortiert.map((item) => ({
    kind: item.kind === 'phase' ? 'phase' : 'task',
    title: item.title,
    parentIndex: item.parentItemId ? (indexById.get(item.parentItemId) ?? null) : null,
    offsetDays: item.offsetDays === null ? '' : String(item.offsetDays),
  }));
}
